import { cn } from '@listening/utils/react-helpers';

/**
 * Skeletons on the app's design tokens.
 *
 * The shared `Skeleton` primitive is on shadcn's `bg-muted`, which does not
 * track the `--v2-*` palette, so a loading state built from it reads as a
 * different surface than the content that replaces it. These use the card
 * background instead, and each composition mirrors the real layout of the page
 * it stands in for — a skeleton that does not match its content is just a
 * differently-shaped spinner.
 */
function Block({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <span
      aria-hidden='true'
      style={style}
      className={cn(
        'block animate-pulse rounded-[6px] bg-[var(--v2-card-bg)]',
        className,
      )}
    />
  );
}

/** Wraps a skeleton so assistive tech announces loading, not empty content. */
function Pending({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div aria-busy='true' aria-live='polite' className={className}>
      {children}
    </div>
  );
}

const keys = (prefix: string, count: number) =>
  Array.from({ length: count }, (_, index) => `${prefix}-${String(index)}`);

/** Mirrors `HomeV2`: folder strip, header row, then the card grid. */
export function LibrarySkeleton() {
  return (
    <Pending className='flex flex-col gap-[var(--v2-gap-6)]'>
      <div className='flex flex-col gap-[var(--v2-gap-3)]'>
        <Block className='h-2.5 w-24' />
        <div className='flex items-center gap-[var(--v2-grid-gap)]'>
          <Block className='size-[calc(var(--v2-folder-h)*0.68)] shrink-0 rounded-full' />
          <div
            className='grid min-w-0 flex-1 gap-[var(--v2-grid-gap)]'
            style={{ gridTemplateColumns: 'repeat(6, minmax(0,1fr))' }}
          >
            {keys('folder', 6).map((key) => (
              <Block
                key={key}
                className='h-[var(--v2-folder-h)] rounded-[var(--v2-card-radius)]'
              />
            ))}
          </div>
        </div>
      </div>

      <div className='flex items-center justify-between gap-3'>
        <Block className='h-7 w-[168px]' />
        <div className='flex items-center gap-[var(--v2-gap-3)]'>
          <Block className='h-9 w-[72px] rounded-[var(--v2-button-radius)]' />
          <Block className='h-9 w-[112px] rounded-[var(--v2-button-radius)]' />
        </div>
      </div>

      <CardGridSkeleton />
    </Pending>
  );
}

export function CardGridPending() {
  return (
    <Pending>
      <CardGridSkeleton />
    </Pending>
  );
}

function CardGridSkeleton() {
  return (
    <div
      className='grid gap-[var(--v2-grid-gap)]'
      style={{
        gridTemplateColumns:
          'repeat(auto-fill, minmax(var(--v2-grid-min), 1fr))',
      }}
    >
      {keys('card', 6).map((key) => (
        <div
          key={key}
          className='flex flex-col gap-[var(--v2-gap-3)] rounded-[var(--v2-card-radius)] border-[length:var(--v2-border-width)] border-[var(--v2-card-border)] p-[var(--v2-gap-3)]'
        >
          <Block
            className='w-full rounded-[var(--v2-card-radius)]'
            style={{ aspectRatio: 'var(--v2-cover-ratio)' }}
          />
          <div className='flex flex-col gap-2 px-0.5'>
            <Block className='h-4 w-3/4' />
            <Block className='h-3 w-1/2' />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Mirrors the folder page: back-arrow header row, then the card grid. */
export function FolderSkeleton() {
  return (
    <Pending className='flex flex-col gap-[var(--v2-gap-6)]'>
      <div className='flex items-center justify-between gap-3'>
        <div className='flex items-center gap-2'>
          <Block className='size-7 rounded-full' />
          <Block className='h-7 w-[180px]' />
        </div>
        <div className='flex items-center gap-[var(--v2-gap-3)]'>
          <Block className='h-9 w-[72px] rounded-[var(--v2-button-radius)]' />
          <Block className='h-9 w-[112px] rounded-[var(--v2-button-radius)]' />
        </div>
      </div>
      <CardGridSkeleton />
    </Pending>
  );
}

/** Mirrors `NotesV2`: header, the document rail, then note cards. */
export function NotesSkeleton() {
  return (
    <Pending className='flex flex-col gap-[var(--v2-gap-6)]'>
      <div className='flex items-center justify-between gap-3'>
        <Block className='h-7 w-[120px]' />
        <div className='flex items-center gap-[var(--v2-gap-3)]'>
          <Block className='h-9 w-[260px] rounded-[var(--v2-button-radius)]' />
          <Block className='h-9 w-[112px] rounded-[var(--v2-button-radius)]' />
        </div>
      </div>
      <div className='flex flex-col gap-[var(--v2-gap-6)] lg:flex-row'>
        <div className='flex shrink-0 flex-col gap-[var(--v2-gap-2)] lg:w-[340px]'>
          <Block className='h-2.5 w-[150px]' />
          <div className='flex flex-col gap-1'>
            {keys('doc', 5).map((key) => (
              <div
                key={key}
                className='flex items-center gap-[var(--v2-gap-3)] p-[var(--v2-gap-3)]'
              >
                <Block
                  className='h-[var(--v2-row-thumb-h)] shrink-0'
                  style={{ aspectRatio: 'var(--v2-row-cover-ratio)' }}
                />
                <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
                  <Block className='h-3.5 w-4/5' />
                  <Block className='h-2.5 w-1/3' />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className='flex min-w-0 flex-1 flex-col gap-[var(--v2-gap-4)]'>
          <div className='flex flex-col gap-2 border-b border-b-[var(--v2-sidebar-border)] pb-[var(--v2-gap-4)]'>
            <Block className='h-6 w-2/3' />
          </div>
          {keys('note', 3).map((key) => (
            <div
              key={key}
              className='flex flex-col gap-2.5 rounded-[var(--v2-card-radius)] border-[length:var(--v2-border-width)] border-[var(--v2-card-border)] p-[var(--v2-gap-4)]'
            >
              <Block className='h-3 w-full' />
              <Block className='h-3 w-11/12' />
              <Block className='h-3 w-3/5' />
            </div>
          ))}
        </div>
      </div>
    </Pending>
  );
}

/** Mirrors a settings pane: heading, then rows inside one card. */
export function SettingsRowsSkeleton({ rows = 2 }: { rows?: number }) {
  return (
    <Pending className='flex flex-col gap-[var(--v2-gap-4)]'>
      <div className='flex flex-col gap-1.5'>
        <Block className='h-5 w-[220px]' />
        <Block className='h-3.5 w-[320px]' />
      </div>
      <div className='flex flex-col overflow-hidden rounded-[var(--v2-card-radius)] border-[length:var(--v2-border-width)] border-[var(--v2-card-border)]'>
        {keys('row', rows).map((key) => (
          <div
            key={key}
            className='flex items-center justify-between gap-[var(--v2-gap-4)] border-b border-b-[var(--v2-card-border)] p-[var(--v2-gap-4)] last:border-b-0'
          >
            <div className='flex min-w-0 flex-col gap-1.5'>
              <Block className='h-3.5 w-[180px]' />
              <Block className='h-3 w-[260px]' />
            </div>
            <Block className='h-9 w-[140px] shrink-0 rounded-[var(--v2-button-radius)]' />
          </div>
        ))}
      </div>
    </Pending>
  );
}

/**
 * The grouped voice list on its own. The pane renders its real header while the
 * voices are still loading, so the inline slot must not repeat it — only the
 * route-level `VoicePaneSkeleton` below adds the heading.
 */
export function VoiceListSkeleton() {
  return (
    <Pending className='flex flex-col gap-[var(--v2-gap-4)]'>
      {[
        { label: 'free', width: 'w-20', count: 4 },
        { label: 'premium', width: 'w-28', count: 5 },
      ].map((group) => (
        <div key={group.label} className='flex flex-col gap-2'>
          <Block className={cn('h-2.5', group.width)} />
          <div className='flex flex-col gap-1'>
            {keys(group.label, group.count).map((key) => (
              <div
                key={key}
                className='flex items-center gap-[var(--v2-gap-3)] rounded-[var(--v2-card-radius)] border-[length:var(--v2-border-width)] border-[var(--v2-card-border)] p-[var(--v2-gap-3)]'
              >
                <Block className='size-9 shrink-0 rounded-full' />
                <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
                  <Block className='h-3.5 w-[120px]' />
                  <Block className='h-2.5 w-[80px]' />
                </div>
                <Block className='h-1.5 w-1/3 shrink-0 rounded-full' />
              </div>
            ))}
          </div>
        </div>
      ))}
    </Pending>
  );
}

/** The whole voice pane, for the route-level pending state. */
export function VoicePaneSkeleton() {
  return (
    <div className='flex flex-col gap-[var(--v2-gap-4)]'>
      <div className='flex flex-col gap-1.5'>
        <Block className='h-5 w-[160px]' />
        <Block className='h-3.5 w-[380px]' />
      </div>
      <VoiceListSkeleton />
      <SettingsRowsSkeleton rows={2} />
    </div>
  );
}
